import { useSnackbar } from 'notistack'
import { useCallback, useState } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import {
  createLearningContinua as createLearningContinuaService,
  importLearningContinuum as importLearningContinuumService,
  cloneLearningContinua as cloneLearningContinuaService,
  updateLearningContinua as updateLearningContinuaService,
  deleteLearningContinua as deleteLearningContinuaService,
  batchDeleteLearningContinua as batchDeleteLearningContinuaService,
  archiveLearningContinua as archiveLearningContinuaService,
  getLearningContinuumSkipLimit as getLearningContinuumSkipLimitService,
  getLearningContinuaById
} from '../../services/learning-continuum/learning-continuum.service.js'
import { validate } from '../../utils'
import { handlerErrorV2 } from '../../utils/errors.jsx'
import {
  updateLearningContinuumForm,
  updateLearningContinuumFormField,
  updateLearningContinuumList,
  updateLearningContinuumListMethod
} from '../../store/actions/learning-continuum.actions.js'
import isEmpty from 'lodash/isEmpty'
import { toggleAlertInfo, toggleAlertModule } from '../../store/actions/alert.actions'
import { useParams } from 'react-router-dom'
import globalConfig from '../../config/globalConfig.js'
import axios from 'axios'
import {
  setCurrentCurriculum,
  updateAssessmentCurriculumMethod
} from '../../store/actions/assessment.actions.js'

const useLearningContinuum = () => {
  const { curriculumId } = useParams()
  const currentCurriculum = useSelector((state) => state?.assessment?.currentCurriculum || {})

  const { enqueueSnackbar } = useSnackbar()
  const dispatch = useDispatch()
  const store = useStore()
  const [isLoadingList, setIsLoadingList] = useState(true)
  const [isLoading, setIsLoading] = useState(false)

  const getLearningContinua = async (curriculumId, drId) => {
    try {
      const response = await getLearningContinuaById(curriculumId, drId)
      if (!response?.data && response?.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        }
      } else {
        const formData = response?.data || {}
        formData.levelsElements = formData.levels
        formData.levels = formData.levels.reduce((acc, level) => {
          acc[level.levelId] = level.definition
          return acc
        }, {})
        dispatch(updateLearningContinuumForm(formData))
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const getLearningContinuaForExport = async (curriculumId, type, filter, columns) => {
    try {
      dispatch(
        toggleAlertInfo(
          true,
          'Exporting Learning Continuum',
          'Please wait as this may take a few minutes',
          'loading'
        )
      )
      const endpoint = `/assessment/${curriculumId}/learning-continuum/list/export/${type}`
      const fallbackFilename = `learning_continua_export.${type}`
      const url = `${globalConfig.axios.apiUrl}${endpoint}`

      const response = await axios.post(
        url,
        {
          filter,
          columns
        },
        {
          responseType: 'blob',
          withCredentials: true, // importante para cookies
          headers: {
            Accept: 'application/octet-stream'
          }
        }
      )

      // extraer filename de Content-Disposition si existe
      const contentDisposition =
        response.headers?.['content-disposition'] || response.headers?.['Content-Disposition']
      const getFilenameFromContentDisposition = (cd) => {
        if (!cd) return null
        // intenta filename*=UTF-8''encoded or filename="name"
        const m = cd.match(/filename\*=UTF-8''([^;]+)|filename="([^"]+)"|filename=([^;]+)/i)
        if (!m) return null
        const raw = m[1] || m[2] || m[3]
        try {
          return decodeURIComponent(raw.replace(/(^"|"$)/g, ''))
        } catch {
          return raw.replace(/(^"|"$)/g, '')
        }
      }

      const filename = getFilenameFromContentDisposition(contentDisposition) || fallbackFilename

      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream'
      })

      // Forzar descarga en navegador
      const blobUrl = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(blobUrl)

      // retornar el blob por si el caller prefiere manejarlo
      return { ok: true, blob, filename }
    } catch (err) {
      // Normalizar error
      const message = err?.response?.data?.error?.message || err?.message || 'Download failed'
      throw new Error(message)
    } finally {
      dispatch(toggleAlertInfo(false))
    }
  }

  const getLearningContinuum = useCallback(async (curriculumId, skip, rowsPerPage, filter) => {
    if (curriculumId) {
      setIsLoadingList(true)
      try {
        const filterData = {
          lcElements: filter.lcElements,
          curriculumElements: filter.curriculumElements,
          lcIds: filter.lcIds
        }
        const response = await getLearningContinuumSkipLimitService(
          curriculumId,
          skip,
          rowsPerPage,
          filterData
        )
        if (!response?.data && response?.error) {
          enqueueSnackbar(response.error.message || 'An error ocurred', { variant: 'error' })
        } else {
          dispatch(updateLearningContinuumList(curriculumId, response?.data || { users: [], count: 0 }))
        }
      } catch (error) {
        const message = handlerErrorV2(error)
        enqueueSnackbar(message, { variant: 'error' })
      } finally {
        setIsLoadingList(false)
      }
    }
  }, [])

  // After an import the backend has new curriculum elements and levels: bring the list back with the current page and filter,
  // otherwise the list keeps showing the rows it had before the import until the user leaves and comes back.
  const refreshLearningContinuumList = () => {
    const state = store.getState()
    getLearningContinuum(
      curriculumId,
      state?.datatable?.lc?.skip || 0,
      state?.datatable?.lc?.rowsPerPage || 50,
      state?.filter?.lc?.filter || {}
    )
  }

  const validateForm = (form) => {
    const fieldRules = {}

    const errors = validate(fieldRules, form)
    if (!isEmpty(errors)) {
      return { error: true, ...errors }
    }

    return { error: false }
  }

  const formData = (form) => {
    const formData = { ...form }

    formData.levels = currentCurriculum.levels.map((level) => {
      return {
        levelId: level._id,
        definition: form?.levels[level._id] || ''
      }
    })

    return formData
  }

  const createLearningContinua = async (form = {}) => {
    const result = validateForm(form)
    if (result.error) {
      return result
    }

    try {
      const data = formData(form)
      const response = await createLearningContinuaService(curriculumId, data)
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      } else {
        dispatch(updateLearningContinuumListMethod(curriculumId, 'POST', response.data))
        enqueueSnackbar('Learning continua created successfully', { variant: 'success' })
        dispatch(toggleAlertModule())

        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const importLearningContinuum = async (file, form) => {
    dispatch(
      toggleAlertInfo(
        true,
        'Importing file to Learning Continuum',
        'Please wait as this may take a few minutes',
        'loading'
      )
    )
    try {
      const data = new FormData()
      if (file) {
        data.append('file', file.blob)
      }
      if (form) {
        data.append('createMissing', String(form.createMissing || 'false'))
      }
      const endpoint = `/assessment/${curriculumId}/learning-continuum/import`
      const url = `${globalConfig.axios.apiUrl}${endpoint}`

      const response = await axios
        .post(url, data, {
          withCredentials: true, // importante para cookies
          headers: {
            Accept: 'application/octet-stream'
          }
        })
        .then((res) => res.data)

      if (!response?.data && response?.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      }
      // the import may have created curriculum elements: the rows resolve their names from the curriculum in the store
      if (response?.data?.currentCurriculum) {
        dispatch(updateAssessmentCurriculumMethod('PUT', response.data.currentCurriculum))
        dispatch(setCurrentCurriculum(curriculumId))
      }
      return { error: false }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    } finally {
      setTimeout(() => {
        dispatch(setCurrentCurriculum(curriculumId))
        refreshLearningContinuumList()
        dispatch(toggleAlertInfo(false))
      }, 3000)
    }
  }

  const importLearningContinuumType = async (file, type) => {
    dispatch(
      toggleAlertInfo(
        true,
        `Importing file to Learning Continuum - ${type}`,
        'Please wait as this may take a few minutes',
        'loading'
      )
    )
    try {
      const data = new FormData()
      if (file) {
        data.append('file', file.blob)
      }
      const endpoint = `/assessment/${curriculumId}/learning-continuum/import/${type}`
      const url = `${globalConfig.axios.apiUrl}${endpoint}`

      const response = await axios
        .post(url, data, {
          withCredentials: true, // importante para cookies
          headers: {
            Accept: 'application/octet-stream'
          }
        })
        .then((res) => res.data)

      if (!response?.data && response?.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      }
      return { error: false }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    } finally {
      setTimeout(() => {
        refreshLearningContinuumList()
        dispatch(toggleAlertInfo(false))
      }, 3000)
    }
  }
  const cloneLearningContinua = async (lcId) => {
    try {
      const response = await cloneLearningContinuaService(curriculumId, lcId)
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      } else {
        dispatch(updateLearningContinuumListMethod(curriculumId, 'POST', response.data))
        enqueueSnackbar('Learning continua cloned successfully', { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const updateLearningContinua = async (lcId, type, form = {}) => {
    if (!['notes', 'level', 'lc-elements', 'curriculum-elements'].includes(type)) {
      return { error: true, message: 'Update not allowed' }
    }

    const result = validateForm(form)
    if (result.error) {
      return result
    }

    try {
      if (type === 'level') {
        dispatch(
          toggleAlertInfo(
            true,
            'Updating Learning Continua',
            'Please wait as this may take a few minutes',
            'loading'
          )
        )
      }
      const data = formData(form)
      const response = await updateLearningContinuaService(curriculumId, lcId, type, data)
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      } else {
        dispatch(updateLearningContinuumListMethod(curriculumId, 'PUT', response.data))
        dispatch(updateLearningContinuumFormField('version', response.data.version))
        enqueueSnackbar('Learning continua updated successfully', { variant: 'success' })
        dispatch(toggleAlertModule())

        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    } finally {
      dispatch(toggleAlertInfo(false))
    }
  }

  const deleteLearningContinua = async (lcId) => {
    try {
      const response = await deleteLearningContinuaService(curriculumId, lcId)
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      } else {
        dispatch(updateLearningContinuumListMethod(curriculumId, 'DELETE', lcId))
        enqueueSnackbar('Learning continua deleted successfully', { variant: 'success' })

        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const batchDeleteLearningContinua = async (lcIds = []) => {
    try {
      const response = await batchDeleteLearningContinuaService(curriculumId, lcIds)
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      }

      const { deleted = [], blocked = [] } = response.data || {}

      // Remove the actually-deleted rows from the list.
      if (deleted.length > 0) {
        dispatch(updateLearningContinuumListMethod(curriculumId, 'BATCH_DELETE', deleted))
      }

      // MF-531 feedback: two clear messages — deleted vs nothing-deleted.
      if (deleted.length > 0) {
        enqueueSnackbar('Learning Continua deleted successfully', { variant: 'success' })
      } else {
        enqueueSnackbar('Nothing deleted - Learning Continua in use by a rubric or with marking history', {
          variant: 'warning'
        })
      }

      return { error: false, deleted, blocked }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const archiveLearningContinua = async (lcId) => {
    try {
      const response = await archiveLearningContinuaService(curriculumId, lcId)
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      } else {
        dispatch(updateLearningContinuumListMethod(curriculumId, 'DELETE', lcId))
        enqueueSnackbar('Learning continua archived successfully', { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  return {
    isLoadingList,
    isLoading,
    getLearningContinuum,
    getLearningContinua,
    createLearningContinua,
    importLearningContinuum,
    importLearningContinuumType,
    cloneLearningContinua,
    updateLearningContinua,
    deleteLearningContinua,
    batchDeleteLearningContinua,
    archiveLearningContinua,
    getLearningContinuaForExport
  }
}

export default useLearningContinuum
